import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  brandsService,
  categoriesService,
  partnerGradesService,
  productsService,
  subcategoriesService,
} from '../services/api';
import { usersService } from '../services/usersService';

const targetTypeLabels = {
  all: 'Tous les produits',
  product: 'Produit',
  category: 'Catégorie',
  subcategory: 'Sous-catégorie',
  brand: 'Marque',
};

const discountTypeLabels = {
  percentage: 'Pourcentage',
  fixed: 'Montant fixe (HT)',
};

const initialGradeForm = {
  name: '',
  description: '',
};

const initialRuleForm = {
  name: '',
  targetType: 'all',
  targetId: '',
  discountType: 'percentage',
  discountValue: '',
};

export default function PartnerDiscounts() {
  const [loading, setLoading] = useState(true);
  const [savingGrade, setSavingGrade] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);

  const [grades, setGrades] = useState([]);
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [gradeForm, setGradeForm] = useState(initialGradeForm);
  const [ruleForm, setRuleForm] = useState(initialRuleForm);

  const [users, setUsers] = useState([]);
  const [userAssignments, setUserAssignments] = useState({});

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const selectedGrade = useMemo(
    () => grades.find((g) => Number(g.id) === Number(selectedGradeId)) || null,
    [grades, selectedGradeId]
  );

  const targetOptions = useMemo(() => {
    if (ruleForm.targetType === 'product') return products.map((p) => ({ id: p.id, name: p.name }));
    if (ruleForm.targetType === 'category') return categories.map((c) => ({ id: c.id, name: c.name || c.title }));
    if (ruleForm.targetType === 'subcategory') return subcategories.map((s) => ({ id: s.id, name: s.name }));
    if (ruleForm.targetType === 'brand') return brands.map((b) => ({ id: b.id, name: b.name }));
    return [];
  }, [ruleForm.targetType, products, categories, subcategories, brands]);

  const getTargetLabel = (rule) => {
    if (rule.targetType === 'all') return targetTypeLabels.all;
    const fromType =
      rule.targetType === 'product'
        ? products
        : rule.targetType === 'category'
          ? categories
          : rule.targetType === 'subcategory'
            ? subcategories
            : brands;
    const found = fromType.find((x) => Number(x.id) === Number(rule.targetId));
    if (!found) return `${targetTypeLabels[rule.targetType]} #${rule.targetId}`;
    return found.name || found.title || `${targetTypeLabels[rule.targetType]} #${rule.targetId}`;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [gradesRes, productsRes, categoriesRes, subcategoriesRes, brandsRes, usersRes] = await Promise.all([
        partnerGradesService.list(),
        productsService.getAll({ page: 1, limit: 500 }),
        categoriesService.getAll(),
        subcategoriesService.getAll(),
        brandsService.getAll(),
        usersService.getAllUsers(1, 200, ''),
      ]);

      const fetchedGrades = gradesRes.data?.grades || [];
      setGrades(fetchedGrades);
      if (!selectedGradeId && fetchedGrades.length > 0) {
        setSelectedGradeId(String(fetchedGrades[0].id));
      }

      setProducts(productsRes.data?.products || []);
      setCategories(categoriesRes.data || []);
      setSubcategories(subcategoriesRes.data || []);
      setBrands(brandsRes.data || []);
      setUsers(usersRes.users || []);

      const assignmentMap = {};
      (usersRes.users || []).forEach((u) => {
        assignmentMap[u.id] = u.partnerGradeId ? String(u.partnerGradeId) : '';
      });
      setUserAssignments(assignmentMap);
    } catch (error) {
      console.error(error);
      alert('Erreur lors du chargement des remises partenaires.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateGrade = async () => {
    if (!gradeForm.name.trim()) return;
    setSavingGrade(true);
    try {
      await partnerGradesService.create({
        name: gradeForm.name.trim(),
        description: gradeForm.description.trim() || null,
        isActive: true,
      });
      setGradeForm(initialGradeForm);
      await loadData();
    } catch (error) {
      console.error(error);
      alert("Impossible de créer le grade partenaire.");
    } finally {
      setSavingGrade(false);
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    if (!window.confirm('Supprimer ce grade partenaire ?')) return;
    try {
      await partnerGradesService.delete(gradeId);
      if (String(gradeId) === String(selectedGradeId)) {
        setSelectedGradeId('');
      }
      await loadData();
    } catch (error) {
      console.error(error);
      alert('Impossible de supprimer ce grade.');
    }
  };

  const handleCreateRule = async () => {
    if (!selectedGrade) return;
    if (!ruleForm.discountValue || Number(ruleForm.discountValue) <= 0) {
      alert('La valeur de remise doit être supérieure à 0.');
      return;
    }
    if (ruleForm.targetType !== 'all' && !ruleForm.targetId) {
      alert('Sélectionne une cible.');
      return;
    }
    setSavingRule(true);
    try {
      await partnerGradesService.createRule(selectedGrade.id, {
        name: ruleForm.name.trim() || null,
        targetType: ruleForm.targetType,
        targetId: ruleForm.targetType === 'all' ? null : Number(ruleForm.targetId),
        discountType: ruleForm.discountType,
        discountValue: Number(ruleForm.discountValue),
        isActive: true,
      });
      setRuleForm(initialRuleForm);
      await loadData();
    } catch (error) {
      console.error(error);
      alert("Impossible d'ajouter la règle.");
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!selectedGrade) return;
    if (!window.confirm('Supprimer cette règle ?')) return;
    try {
      await partnerGradesService.deleteRule(selectedGrade.id, ruleId);
      await loadData();
    } catch (error) {
      console.error(error);
      alert('Impossible de supprimer la règle.');
    }
  };

  const handleAssignUser = async (userId) => {
    setSavingUserId(userId);
    try {
      await partnerGradesService.assignUser(userId, userAssignments[userId] ? Number(userAssignments[userId]) : null);
      await loadData();
    } catch (error) {
      console.error(error);
      alert("Impossible d'affecter le grade partenaire.");
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Remises Partenaires
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Crée des grades partenaires, configure leurs règles de remise et affecte ces grades aux utilisateurs.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Créer un grade partenaire
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Nom du grade"
              value={gradeForm.name}
              onChange={(e) => setGradeForm((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ minWidth: 240 }}
            />
            <TextField
              label="Description"
              value={gradeForm.description}
              onChange={(e) => setGradeForm((prev) => ({ ...prev, description: e.target.value }))}
              sx={{ minWidth: 340 }}
            />
            <Button variant="contained" onClick={handleCreateGrade} disabled={savingGrade}>
              Ajouter
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Grades partenaires
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Règles</TableCell>
                  <TableCell>Utilisateurs</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grades.map((grade) => (
                  <TableRow key={grade.id} selected={String(grade.id) === String(selectedGradeId)}>
                    <TableCell>
                      <Button size="small" onClick={() => setSelectedGradeId(String(grade.id))}>
                        {grade.name}
                      </Button>
                    </TableCell>
                    <TableCell>{grade.description || '-'}</TableCell>
                    <TableCell>{grade.rules?.length || 0}</TableCell>
                    <TableCell>{grade.$extras?.users_count || 0}</TableCell>
                    <TableCell align="right">
                      <Button color="error" size="small" onClick={() => handleDeleteGrade(grade.id)}>
                        Supprimer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {selectedGrade && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Règles - {selectedGrade.name}
            </Typography>

            <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
              <TextField
                label="Nom de la règle (optionnel)"
                value={ruleForm.name}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, name: e.target.value }))}
                sx={{ minWidth: 240 }}
              />

              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Cible</InputLabel>
                <Select
                  label="Cible"
                  value={ruleForm.targetType}
                  onChange={(e) => setRuleForm((prev) => ({ ...prev, targetType: e.target.value, targetId: '' }))}
                >
                  {Object.entries(targetTypeLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {ruleForm.targetType !== 'all' && (
                <FormControl sx={{ minWidth: 240 }}>
                  <InputLabel>Élément ciblé</InputLabel>
                  <Select
                    label="Élément ciblé"
                    value={ruleForm.targetId}
                    onChange={(e) => setRuleForm((prev) => ({ ...prev, targetId: e.target.value }))}
                  >
                    {targetOptions.map((option) => (
                      <MenuItem key={option.id} value={String(option.id)}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Type de remise</InputLabel>
                <Select
                  label="Type de remise"
                  value={ruleForm.discountType}
                  onChange={(e) => setRuleForm((prev) => ({ ...prev, discountType: e.target.value }))}
                >
                  {Object.entries(discountTypeLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={ruleForm.discountType === 'percentage' ? 'Valeur (%)' : 'Valeur (HT)'}
                type="number"
                value={ruleForm.discountValue}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, discountValue: e.target.value }))}
                sx={{ width: 140 }}
              />

              <Button variant="contained" onClick={handleCreateRule} disabled={savingRule}>
                Ajouter la règle
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Règle</TableCell>
                    <TableCell>Cible</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Valeur</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedGrade.rules || []).map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>{rule.name || `Règle #${rule.id}`}</TableCell>
                      <TableCell>{getTargetLabel(rule)}</TableCell>
                      <TableCell>{discountTypeLabels[rule.discountType] || rule.discountType}</TableCell>
                      <TableCell>
                        {rule.discountType === 'percentage'
                          ? `${Number(rule.discountValue).toFixed(2)} %`
                          : `${Number(rule.discountValue).toFixed(2)} €`}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={rule.isActive ? 'success' : 'default'}
                          label={rule.isActive ? 'Actif' : 'Inactif'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button color="error" size="small" onClick={() => handleDeleteRule(rule.id)}>
                          Supprimer
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Affectation des grades aux utilisateurs
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Les remises partenaires sont appliquées automatiquement au checkout côté serveur.
          </Alert>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Grade actuel</TableCell>
                  <TableCell>Nouveau grade</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.first_name || '-'} {user.last_name || ''}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.partnerGrade?.name || 'Aucun'}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          value={userAssignments[user.id] ?? ''}
                          onChange={(e) =>
                            setUserAssignments((prev) => ({
                              ...prev,
                              [user.id]: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="">Aucun</MenuItem>
                          {grades.map((grade) => (
                            <MenuItem key={grade.id} value={String(grade.id)}>
                              {grade.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAssignUser(user.id)}
                        disabled={savingUserId === user.id}
                      >
                        {savingUserId === user.id ? 'Sauvegarde...' : 'Enregistrer'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

